// Admin console DTOs + inputs (dashboard, users). docs/replan-v2.md (admin).
// Staff-facing — English chrome, but inputs are still validated server-side.

import { z } from 'zod';

// ─── Users list ───────────────────────────────────────────────────────────────

export const userStatusFilter = z.enum(['all', 'active', 'suspended', 'invited']);
export type UserStatusFilter = z.infer<typeof userStatusFilter>;

export const userListQuery = z.object({
  q: z.string().trim().max(120).optional(),
  status: userStatusFilter.optional().default('all'),
  // 'adult' = has email; 'learner' = username-only (kids/students).
  kind: z.enum(['all', 'adult', 'learner']).optional().default('all'),
  limit: z.coerce.number().int().min(1).max(100).optional().default(50),
  offset: z.coerce.number().int().min(0).optional().default(0),
});
export type UserListQuery = z.infer<typeof userListQuery>;

export const adminUserRowDto = z.object({
  id: z.uuid(),
  email: z.string().nullable(),
  username: z.string().nullable(),
  displayName: z.string().nullable(),
  status: z.enum(['active', 'suspended', 'invited']),
  locale: z.enum(['ar', 'en']),
  roles: z.array(z.string()), // resolved role labels: parent, kid, teacher:…, super_admin, …
  createdAt: z.string(),
});
export type AdminUserRowDto = z.infer<typeof adminUserRowDto>;

export const adminUserDetailDto = adminUserRowDto.extend({
  country: z.string().nullable(),
  phone: z.string().nullable(),
  emailVerified: z.boolean(),
  deletedAt: z.string().nullable(),
  // Tenancy memberships for the drill-in.
  families: z.array(z.object({ id: z.uuid(), role: z.string() })),
  schools: z.array(z.object({ id: z.uuid(), name: z.string(), role: z.string() })),
});
export type AdminUserDetailDto = z.infer<typeof adminUserDetailDto>;

// ─── Mutations ────────────────────────────────────────────────────────────────

export const adminResetPasswordInput = z.object({
  // Optional explicit new password; when omitted the server generates one and
  // returns it once (for the admin to hand to the user).
  password: z.string().min(6).max(256).optional(),
});
export type AdminResetPasswordInput = z.infer<typeof adminResetPasswordInput>;

// ─── Dashboard ────────────────────────────────────────────────────────────────

// ─── Schools / Families drill-in ──────────────────────────────────────────────

// A subscription summary attached to a school/family in the drill-in.
export const ownerSubscriptionDto = z.object({
  status: z.string(),
  planNameKey: z.string(),
  currency: z.string(),
  interval: z.string(),
  currentPeriodEnd: z.string().nullable(),
});
export type OwnerSubscriptionDto = z.infer<typeof ownerSubscriptionDto>;

export const adminSchoolRowDto = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  country: z.string(),
  status: z.string(),
  teacherCount: z.number(),
  studentCount: z.number(),
  classCount: z.number(),
  subscriptionStatus: z.string().nullable(),
  createdAt: z.string(),
});
export type AdminSchoolRowDto = z.infer<typeof adminSchoolRowDto>;

export const adminSchoolDetailDto = z.object({
  id: z.uuid(),
  name: z.string(),
  slug: z.string(),
  country: z.string(),
  status: z.string(),
  createdAt: z.string(),
  subscription: ownerSubscriptionDto.nullable(),
  admins: z.array(z.object({ id: z.uuid(), label: z.string() })),
  teachers: z.array(z.object({ id: z.uuid(), label: z.string(), status: z.string() })),
  classes: z.array(
    z.object({ id: z.uuid(), name: z.string(), grade: z.string(), studentCount: z.number() }),
  ),
  studentCount: z.number(),
});
export type AdminSchoolDetailDto = z.infer<typeof adminSchoolDetailDto>;

export const adminFamilyRowDto = z.object({
  id: z.uuid(),
  parentLabel: z.string(),
  kidCount: z.number(),
  status: z.string(),
  subscriptionStatus: z.string().nullable(),
  createdAt: z.string(),
});
export type AdminFamilyRowDto = z.infer<typeof adminFamilyRowDto>;

export const adminFamilyDetailDto = z.object({
  id: z.uuid(),
  status: z.string(),
  createdAt: z.string(),
  parent: z.object({ id: z.uuid(), label: z.string(), email: z.string().nullable() }).nullable(),
  kids: z.array(
    z.object({
      id: z.uuid(),
      label: z.string(),
      username: z.string().nullable(),
      status: z.string(),
    }),
  ),
  subscription: ownerSubscriptionDto.nullable(),
});
export type AdminFamilyDetailDto = z.infer<typeof adminFamilyDetailDto>;

// ─── Coupons ──────────────────────────────────────────────────────────────────

// One promotions mechanism, three kinds (owner ruling 2026-08-16):
//   coupon        — customer types a code
//   offer         — no code; auto-applies inside [startsAt, expiresAt]
//   plan_discount — auto-applies to a plan (+ optional interval, which is how
//                   a yearly-only discount is expressed)
export const promotionKindSchema = z.enum(['coupon', 'offer', 'plan_discount']);
export type PromotionKind = z.infer<typeof promotionKindSchema>;

export const couponCreateInput = z
  .object({
    kind: promotionKindSchema.default('coupon'),
    // Required for `coupon`, forbidden for the automatic kinds — enforced by
    // the refinements below so the API rejects a nonsensical combination
    // rather than silently storing an unreachable promotion.
    code: z
      .string()
      .trim()
      .toUpperCase()
      .regex(/^[A-Z0-9][A-Z0-9-]{1,39}$/, 'A-Z, 0-9, dash')
      .nullable()
      .optional(),
    name: z.string().trim().min(1).max(120).nullable().optional(),
    discountPercent: z.number().int().min(1).max(100).nullable().optional(),
    discountAmountMinor: z.number().int().positive().nullable().optional(),
    discountCurrency: z
      .enum(['QAR', 'AED', 'SAR', 'KWD', 'BHD', 'OMR', 'USD'])
      .nullable()
      .optional(),
    maxRedemptions: z.number().int().positive().nullable().optional(),
    maxRedemptionsPerOwner: z.number().int().positive().nullable().optional(),
    startsAt: z.iso.datetime().nullable().optional(),
    expiresAt: z.iso.datetime().nullable().optional(),
    appliesToPlanId: z.uuid().nullable().optional(),
    appliesToInterval: z.enum(['month', 'year']).nullable().optional(),
  })
  .refine((v) => v.discountPercent != null || v.discountAmountMinor != null, {
    message: 'Set a percent or a fixed amount',
  })
  // Exactly one, never both. Both resolvers branch `if (percent) … else if
  // (amount)`, so a row carrying both would silently apply the percent and
  // leave the fixed amount as dead data that nobody can see is being ignored.
  .refine((v) => v.discountPercent == null || v.discountAmountMinor == null, {
    message: 'Set a percent or a fixed amount, not both',
  })
  .refine((v) => v.discountAmountMinor == null || v.discountCurrency != null, {
    message: 'A fixed-amount coupon needs a currency',
  })
  .refine((v) => v.kind !== 'coupon' || (v.code != null && v.code.length > 0), {
    message: 'A coupon needs a code',
    path: ['code'],
  })
  .refine((v) => v.kind === 'coupon' || v.code == null, {
    message: 'An automatic offer must not have a code',
    path: ['code'],
  })
  .refine((v) => v.kind !== 'plan_discount' || v.appliesToPlanId != null, {
    message: 'A plan discount needs a plan',
    path: ['appliesToPlanId'],
  })
  // An automatic promotion with no window would apply forever and silently
  // discount every checkout — make the operator state an end date.
  .refine((v) => v.kind === 'coupon' || v.expiresAt != null, {
    message: 'An automatic offer needs an end date',
    path: ['expiresAt'],
  })
  .refine((v) => v.startsAt == null || v.expiresAt == null || v.startsAt < v.expiresAt, {
    message: 'The start must be before the end',
    path: ['startsAt'],
  });
export type CouponCreateInput = z.infer<typeof couponCreateInput>;

export const couponRowDto = z.object({
  id: z.uuid(),
  kind: promotionKindSchema,
  code: z.string().nullable(),
  name: z.string().nullable(),
  discountPercent: z.number().nullable(),
  discountAmountMinor: z.number().nullable(),
  discountCurrency: z.string().nullable(),
  maxRedemptions: z.number().nullable(),
  maxRedemptionsPerOwner: z.number().nullable(),
  redemptions: z.number(),
  startsAt: z.string().nullable(),
  expiresAt: z.string().nullable(),
  appliesToPlanId: z.uuid().nullable(),
  appliesToInterval: z.string().nullable(),
  status: z.string(),
  createdAt: z.string(),
});
export type CouponRowDto = z.infer<typeof couponRowDto>;

// ─── Audit log ──────────────────────────────────────────────────────────────

export const auditQuery = z.object({
  action: z.string().trim().max(120).optional(),
  actorId: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(200).optional().default(100),
  offset: z.coerce.number().int().min(0).optional().default(0),
});
export type AuditQuery = z.infer<typeof auditQuery>;

export const auditRowDto = z.object({
  id: z.uuid(),
  action: z.string(),
  actorId: z.uuid().nullable(),
  actorLabel: z.string().nullable(),
  targetEntityType: z.string().nullable(),
  targetEntityId: z.uuid().nullable(),
  metadata: z.record(z.string(), z.unknown()),
  ip: z.string().nullable(),
  createdAt: z.string(),
});
export type AuditRowDto = z.infer<typeof auditRowDto>;

// ─── Feature flags ──────────────────────────────────────────────────────────

export const featureFlagDto = z.object({
  name: z.string(),
  enabled: z.boolean(),
  description: z.string(),
});
export type FeatureFlagDto = z.infer<typeof featureFlagDto>;

export const featureFlagUpsertInput = z.object({
  name: z
    .string()
    .trim()
    .regex(/^[a-z][a-z0-9_.]{1,58}$/, 'lowercase dotted flag name'),
  enabled: z.boolean(),
  description: z.string().trim().max(200).optional().default(''),
});
export type FeatureFlagUpsertInput = z.infer<typeof featureFlagUpsertInput>;

// ─── Announcements ────────────────────────────────────────────────────────────

export const announcementInput = z.object({
  // Target segment: a role + optional country (ISO alpha-2).
  role: z.enum(['parent', 'teacher', 'school_admin', 'kid', 'student', 'all']),
  country: z.string().length(2).toUpperCase().nullable().optional(),
  title: z.string().trim().min(1).max(120),
  body: z.string().trim().min(1).max(500),
  // Optional in-app deep link (e.g. /billing).
  link: z.string().trim().max(200).nullable().optional(),
});
export type AnnouncementInput = z.infer<typeof announcementInput>;

export const announcementResultDto = z.object({
  recipients: z.number(),
});
export type AnnouncementResultDto = z.infer<typeof announcementResultDto>;

// ─── Settings (branding + AI defaults, stored in site_settings) ───────────────

export const settingsDto = z.object({
  brandName: z.string(),
  supportEmail: z.string(),
  aiStoryTone: z.string(),
  aiImageStyle: z.string(),
});
export type SettingsDto = z.infer<typeof settingsDto>;

export const settingsUpdateInput = z.object({
  brandName: z.string().trim().max(120).optional(),
  supportEmail: z.string().trim().max(200).optional(),
  aiStoryTone: z.string().trim().max(500).optional(),
  aiImageStyle: z.string().trim().max(500).optional(),
});
export type SettingsUpdateInput = z.infer<typeof settingsUpdateInput>;

// ─── Dashboard ────────────────────────────────────────────────────────────────

export const dashboardDto = z.object({
  users: z.object({
    total: z.number(),
    active: z.number(),
    suspended: z.number(),
    newThisWeek: z.number(),
  }),
  families: z.number(),
  schools: z.number(),
  learners: z.number(),
  subscriptions: z.record(z.string(), z.number()), // status → count
  mrrByCurrency: z.record(z.string(), z.number()), // currency → minor units (monthly-normalized)
  paidInvoices: z.number(),
  lessonsCompleted: z.number(),
  activeLast7d: z.number(),
});
export type DashboardDto = z.infer<typeof dashboardDto>;

// ─── Webhook events (M7f) ────────────────────────────────────────────────────
//
// Read-only view over `payment_events` (motiflink §3.6 calls it
// `payment_webhooks` — stale). `raw_payload` is NOT on the row DTO: it is
// arbitrary jsonb and only crosses the wire on the detail fetch, redacted.

export const webhookEventQuery = z.object({
  q: z.string().trim().max(200).optional(), // matches provider_event_id
  kind: z.string().trim().max(80).optional(),
  provider: z.string().trim().max(40).optional(),
  subscriptionId: z.uuid().optional(),
  from: z.string().optional(), // ISO date, inclusive
  to: z.string().optional(), // ISO date, exclusive
  limit: z.coerce.number().int().min(1).max(200).optional().default(50),
  offset: z.coerce.number().int().min(0).optional().default(0),
});
export type WebhookEventQuery = z.infer<typeof webhookEventQuery>;

export const webhookEventRowDto = z.object({
  id: z.uuid(),
  provider: z.string(),
  providerEventId: z.string().nullable(),
  eventKind: z.string(),
  subscriptionId: z.uuid().nullable(),
  receivedAt: z.string(),
  /** ≤3 allow-listed scalar keys lifted from the payload, already redacted. */
  summary: z.record(z.string(), z.string()),
});
export type WebhookEventRowDto = z.infer<typeof webhookEventRowDto>;

export const webhookEventListDto = z.object({
  rows: z.array(webhookEventRowDto),
  total: z.number().int(),
  limit: z.number().int(),
  offset: z.number().int(),
  /** Distinct values, so the filter chips need no code change per provider. */
  kinds: z.array(z.string()),
  providers: z.array(z.string()),
});
export type WebhookEventListDto = z.infer<typeof webhookEventListDto>;

export const webhookEventDetailDto = webhookEventRowDto.extend({
  /** REDACTED server-side (deny-list by key name, at every depth). */
  payload: z.record(z.string(), z.unknown()),
  /** True when the payload exceeded the response cap and was not serialized. */
  truncated: z.boolean(),
  /** Present when truncated: the top-level keys, so it is still diagnosable. */
  payloadKeys: z.array(z.string()),
});
export type WebhookEventDetailDto = z.infer<typeof webhookEventDetailDto>;

// ─── Promotion status (Track D) ──────────────────────────────────────────────
//
// 🔴 ARCHIVE, NOT EDIT. `PATCH /admin/coupons/:id` accepts a status and
// NOTHING else, deliberately.
//
// A promotion that has been redeemed is a historical fact: `coupon_redemptions`
// rows point at it, and every one of them was priced by the discount this row
// carried AT THE TIME. Letting an operator change the percent, the amount, the
// window or the code would silently rewrite what those past checkouts mean —
// a 50%-off coupon edited to 10% makes the ledger say customers paid a price
// nobody ever offered them. Archiving is reversible and additive; editing is
// neither. To change the terms, archive this one and create a new one.
//
// `archived` is honoured by BOTH resolvers already: resolveAutomaticPromotion
// filters on `status = 'active'`, and the typed-code path rejects a non-active
// row. So archiving takes effect at the next checkout with no other change.
export const couponStatusSchema = z.enum(['active', 'archived']);
export type CouponStatus = z.infer<typeof couponStatusSchema>;

// `.strict()` and not the default strip: a console (or a script) that sends
// `{ status, discountPercent }` expecting an edit must get a LOUD 400, not a
// 200 that quietly ignored half the body and left the operator believing the
// discount changed. Silently dropping a field is how "I definitely edited that
// coupon" becomes a support ticket.
export const couponStatusUpdateInput = z
  .object({
    status: couponStatusSchema,
  })
  .strict();
export type CouponStatusUpdateInput = z.infer<typeof couponStatusUpdateInput>;

export const couponStatusUpdateDto = z.object({
  id: z.uuid(),
  status: couponStatusSchema,
});
export type CouponStatusUpdateDto = z.infer<typeof couponStatusUpdateDto>;
// ─── User edit (Track U) ─────────────────────────────────────────────────────
//
// `PATCH /admin/users/:id` — the first WRITE path onto a user's own identity
// fields. Everything before it (suspend/reactivate/reset-password) changed
// state ABOUT a user; this changes WHO the row says they are.
//
// 🔴 Why every field is `.optional()` and the body is `.strict()`:
// a partial update must be able to say "change only the phone number" without
// the console having to resend an email address it might have loaded from a
// stale cache and would otherwise write back verbatim. `.strict()` then makes
// an unknown key a LOUD 400 rather than a 200 that silently ignored it — the
// same reasoning as `couponStatusUpdateInput` above.
//
// 🔴 `email` and `username` are the auth-critical pair. Changing either
// invalidates the identity the outstanding JWTs were minted with, so the route
// bumps `users.auth_generation` when (and only when) one of them actually
// moves. See apps/api/src/routes/admin/users.ts.
//
// `email: null` is deliberately expressible — a parent row being converted to
// a learner-shaped one — but the DB check constraint `users_email_or_username`
// still refuses to leave a live row with neither, so the route pre-checks and
// returns `invalid_input` rather than letting a 23514 surface as a 500.
export const adminUserUpdateInput = z
  .object({
    // Lower-cased + trimmed here so the uniqueness pre-check and the partial
    // unique index (which is on `lower(email)`) agree on what "taken" means.
    email: z
      .union([
        z
          .email()
          .max(320)
          .transform((v) => v.trim().toLowerCase()),
        z.null(),
      ])
      .optional(),
    // Mirrors the `users_username_format` check constraint exactly. Rejecting
    // here turns a 23514 into a field-level 400 the console can point at.
    username: z
      .union([
        z
          .string()
          .trim()
          .toLowerCase()
          .regex(
            /^[a-z][a-z0-9_.]{2,39}$/,
            'Letters, digits, dot or underscore; start with a letter',
          ),
        z.null(),
      ])
      .optional(),
    displayName: z.union([z.string().trim().min(1).max(120), z.null()]).optional(),
    country: z.union([z.string().trim().toUpperCase().length(2), z.null()]).optional(),
    phone: z.union([z.string().trim().min(3).max(32), z.null()]).optional(),
    locale: z.enum(['ar', 'en']).optional(),
    // `invited` is reachable but suspend/reactivate remain the ordinary path —
    // they exist so a support agent gets a one-click action with its own audit
    // action name instead of a generic "user edited".
    status: z.enum(['active', 'suspended', 'invited']).optional(),
  })
  .strict();
export type AdminUserUpdateInput = z.infer<typeof adminUserUpdateInput>;

export const adminUserUpdateDto = z.object({
  id: z.uuid(),
  /** True when email/username moved and every outstanding token was revoked. */
  sessionsRevoked: z.boolean(),
  /** The field names that actually changed — drives the toast copy. */
  changed: z.array(z.string()),
});
export type AdminUserUpdateDto = z.infer<typeof adminUserUpdateDto>;

// ─── Family invoices (Track U) ───────────────────────────────────────────────
//
// Invoice history for ONE family, across every subscription row it has ever
// held. Read-only: `invoices` is part of the append-only money ledger and the
// admin console has no edit path onto it by design.
//
// 🔴 NO TOTAL FIELD, deliberately. A family can hold invoices in more than one
// currency (a plan change re-prices, and the seven currencies have no FX table
// anywhere in this system). A single `total` would have to either blend them
// into fiction or silently pick one. The console groups by currency instead —
// `sumMinor()` in apps/admin/src/lib/money.ts throws rather than blend, and a
// test asserts that.
export const adminFamilyInvoiceDto = z.object({
  id: z.uuid(),
  subscriptionId: z.uuid(),
  amountMinor: z.number().int(),
  /** Before discount. Null on older rows written before provenance existed. */
  subtotalMinor: z.number().int().nullable(),
  discountMinor: z.number().int(),
  currency: z.string(),
  status: z.string(),
  planSlug: z.string().nullable(),
  issuedAt: z.string(),
  dueAt: z.string().nullable(),
  paidAt: z.string().nullable(),
});
export type AdminFamilyInvoiceDto = z.infer<typeof adminFamilyInvoiceDto>;

// ─── Kid progress (Track U) ──────────────────────────────────────────────────
//
// 🔴 READ-ONLY VIEW OVER A FROZEN ENGINE. The learning engine is frozen this
// push (owner directive, docs/next_session.md): these DTOs are shaped so the
// endpoint behind them can be satisfied entirely by SELECTs against
// learner_progress / activity_attempts / xp_ledger / streaks / enrollments /
// learner_badges. Nothing here implies a write, a schema change, or a new
// column, and the route must never acquire one.
//
// 🔴 CHILDREN'S PRIVACY (CLAUDE.md §4). This is a child's record, so the shape
// is deliberately minimal: counts, timestamps and lesson identifiers only. No
// attempt `payload` (it holds recordings and free text), no answers, no
// third-party-shippable fields. The route audit-logs every read.
export const adminKidAttemptDto = z.object({
  id: z.uuid(),
  activityId: z.uuid(),
  /** The activity's authoring key — an identifier, never child-authored text. */
  activityKind: z.string().nullable(),
  lessonTitleKey: z.string().nullable(),
  score: z.number().int().nullable(),
  /** 0/1 in the column; surfaced as a tri-state (null = not a graded type). */
  correct: z.boolean().nullable(),
  startedAt: z.string(),
  endedAt: z.string().nullable(),
});
export type AdminKidAttemptDto = z.infer<typeof adminKidAttemptDto>;

export const adminKidProgressDto = z.object({
  userId: z.uuid(),
  label: z.string(),
  username: z.string().nullable(),
  status: z.string(),
  lessonsCompleted: z.number().int(),
  lessonsInProgress: z.number().int(),
  /** Sum of xp_ledger.delta. Can only be read, never adjusted from here. */
  xpTotal: z.number().int(),
  streakCurrent: z.number().int(),
  streakLongest: z.number().int(),
  lastActiveDate: z.string().nullable(),
  enrollments: z.number().int(),
  badges: z.number().int(),
  /** Mean of learner_progress.score over scored lessons; null when none. */
  averageScore: z.number().nullable(),
  totalTimeSpentMs: z.number().int(),
  lastLessonAt: z.string().nullable(),
  recentAttempts: z.array(adminKidAttemptDto),
});
export type AdminKidProgressDto = z.infer<typeof adminKidProgressDto>;

export const adminFamilyProgressDto = z.object({
  familyId: z.uuid(),
  kids: z.array(adminKidProgressDto),
});
export type AdminFamilyProgressDto = z.infer<typeof adminFamilyProgressDto>;
